import React, { useCallback, useEffect, useState } from 'react';
import PageHeaderText from 'components/base/page-header-text';
import RequestListFilters from 'components/base/request-list-filters';
import { ENUM_REQUEST_STATUS } from 'models/common.model';
import { MAIN_STORE_ROUTE_KEYS } from 'utils/constants';
import { debounce } from 'utils/functions';
import { Button, ToastMessage } from '@cokitchen/cokitchen-components';
import { toast } from 'react-toastify';
import { apiService } from 'services/base.service';
import { ReactComponent as NoProcurement } from 'assets/icons/no-procurement.svg';
import BrandChefsRequestCard from 'components/brand-chefs-request-card';
import ActivityIndicator from 'components/base/loader/activityIndicator';
import { useNavigate } from 'react-router-dom';
import useBrandChefsContext from 'contexts/brand-chefs.context';
import { DefaultItemToEdit } from 'contexts/brand-chefs.context/provider';
import useDashboardContext from 'contexts/dashboard.context';
import { PrepRequestType } from 'models/prep-request.model';
import { SocketIOConnection } from 'utils/socket-config';
import useRoleContext from 'contexts/role.context';
import { InventoryModule, RolePermission } from 'models/permission.model';

export type ExtendedRequestType = ENUM_REQUEST_STATUS;

type RequestStatusFilter = ENUM_REQUEST_STATUS;

export type FilterType = {
  search: string;
  status: RequestStatusFilter[];
  dateRange: { startDate: Date; endDate: Date };
  current_page: number;
};
const ManageRequests = () => {
  const navigate = useNavigate();
  const [filteredManageRequests, setFilteredManageRequests] = useState<PrepRequestType[]>();
  const [isFetchingRequests, setIsFetchingRequests] = useState(true);
  const [lastPage, setLastPage] = useState(1);
  const [filters, setFilters] = useState<FilterType>({
    search: '',
    status: [],
    dateRange: {
      startDate: new Date(),
      endDate: new Date()
    },
    current_page: 1
  });
  const { activeStore } = useDashboardContext();
  const { setItemToEdit, setStep } = useBrandChefsContext();
  const { can } = useRoleContext();
  const canWriteKitchenRequisition = can(InventoryModule.KitchenRequisition, RolePermission.Write);
  const clearItemsList = () => {
    setFilteredManageRequests([]);
  };
  const initSocket = async () => {
    const conn = await SocketIOConnection.getConnection();
    const roomName = `brand_chef_request:${activeStore?.id}`;

    conn.onReconnect(async () => {
      conn.joinRoom(roomName, process.env.REACT_APP_WEBOCKETAPP_KEY);
    });

    conn.addEventListener(roomName, 'brand_chef_request_updated', (data: PrepRequestType) => {
      setFilteredManageRequests((prev) => {
        if (!prev) return [];
        const findRequest = prev?.find((item) => item.id === data.id);
        if (findRequest) {
          return prev?.map((item) => (item.id === data.id ? { ...item, ...data } : item));
        } else {
          return [data, ...prev];
        }
      });
    });
    conn.joinRoom(roomName, process.env.REACT_APP_WEBOCKETAPP_KEY);
    return () => {
      conn.removeListener(roomName, 'brand_chef_request_updated');
    };
  };
  const removeListener = async () => {
    const conn = await SocketIOConnection.getConnection();
    const roomName = `brand_chef_request:${activeStore?.id}`;
    conn.removeListener(roomName, 'brand_chef_request_updated');
  };
  useEffect(() => {
    if (!activeStore?.id) return;
    initSocket();
    return () => {
      removeListener();
    };
  }, []);
  useEffect(() => {
    if (!activeStore?.id) return;
    initSocket();
    return () => {
      removeListener();
    };
  }, []);

  useEffect(() => {
    setIsFetchingRequests(true);
    clearItemsList();
    debounceFetchVendors(filters.search, filters.status, filters.current_page);
  }, [filters, activeStore?.id]);

  const fetchRequests = async (
    searchString: string,
    statusValues: RequestStatusFilter[],
    current_page?: number
  ) => {
    if (!activeStore) return;
    try {
      setIsFetchingRequests(true);
      const normalizedStatus = statusValues;
      const statusParam =
        normalizedStatus.length === 0
          ? undefined
          : normalizedStatus.length === 1
          ? normalizedStatus[0]
          : normalizedStatus;

      const response = await apiService.get(`prep-kitchen-request/stores/${activeStore.id}`, {
        version: 'v2',
        params: {
          item_type: 'meal',
          ...(searchString ? { code: searchString } : {}),
          ...(statusParam ? { status: statusParam } : {}),
          page: current_page || '1'
        }
      });
      if (response) {
        setFilteredManageRequests(response?.data?.data);
        setLastPage(Math.ceil(response?.data?.pagination?.total / 10));
      }
    } catch (err: any) {
      toast(
        <ToastMessage
          message={err.response?.data?.message || 'Error fetching vendors'}
          type='error'
        />
      );
    } finally {
      setIsFetchingRequests(false);
    }
  };

  const debounceFetchVendors = useCallback(debounce(fetchRequests, 1000), []);
  const handleRequest = () => {
    setItemToEdit([DefaultItemToEdit]);
    setStep(1);
    navigate(MAIN_STORE_ROUTE_KEYS.REQUEST_MANAGE_MEALS);
  };
  return (
    <div>
      <PageHeaderText title='Manage Requests' description='Manage kitchen item requests'>
        {canWriteKitchenRequisition && (
          <Button
            onClick={handleRequest}
            variant='block'
            color='primary'
            className='w-[167px] text-white'
            small>
            New Request
          </Button>
        )}
      </PageHeaderText>
      <RequestListFilters
        statusMode='multi'
        statusOptions={[
          { text: ENUM_REQUEST_STATUS.REQUESTED, value: ENUM_REQUEST_STATUS.REQUESTED },
          { text: ENUM_REQUEST_STATUS.CANCELLED, value: ENUM_REQUEST_STATUS.CANCELLED },
          { text: ENUM_REQUEST_STATUS.SENT, value: ENUM_REQUEST_STATUS.SENT },
          { text: ENUM_REQUEST_STATUS.RECEIVED, value: ENUM_REQUEST_STATUS.RECEIVED },
          { text: ENUM_REQUEST_STATUS.REVIEWED, value: ENUM_REQUEST_STATUS.REVIEWED }
        ]}
        filters={filters}
        onFilterChange={setFilters}
        searchWithinGroup
        pageCount={lastPage}
      />
      {isFetchingRequests ? (
        <div className='flex h-[calc(100vh-200px)] items-center justify-center'>
          <ActivityIndicator />
        </div>
      ) : !filteredManageRequests?.length ? (
        <div className='flex flex-col items-center justify-center h-[calc(100vh-200px)]'>
          <NoProcurement className='mb-5' />
          <p className='font-medium text-[#5E6164]'>There are currently no requests</p>
        </div>
      ) : (
        <div className='mt-10 gap-4 grid grid-cols-3 max-[1250px]:grid-cols-2 max-[768px]:grid-cols-1'>
          {filteredManageRequests?.map((p, i) => (
            <div key={i}>
              <BrandChefsRequestCard key={i} request={p} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ManageRequests;
